'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { DescriptionDetails } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionDetails';
import { DescriptionList } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionList';
import { DescriptionTerm } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionTerm';
import { Alert } from '@algoacademy/design-system/web/feedback/Alert/Alert';
import { AlertActions } from '@algoacademy/design-system/web/feedback/Alert/AlertActions';
import { AlertBody } from '@algoacademy/design-system/web/feedback/Alert/AlertBody';
import { AlertDescription } from '@algoacademy/design-system/web/feedback/Alert/AlertDescription';
import { AlertTitle } from '@algoacademy/design-system/web/feedback/Alert/AlertTitle';
import { formatWarsawLongDate } from '@algoacademy/shared/warsaw-calendar';
import { formatZloty } from '../_helpers/mentoring-terms';
export type ChangeMentoringConfirmationMode = 'schedule' | 'replace' | 'takeBack' | 'unchangedInForce' | 'unchangedScheduled';
type ChangeMentoringConfirmAlertProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  studentName: string;
  currentTermsSummary: string;
  newTermsSummary: string;
  newPriceZloty: number;
  renewsAt: Date;
  mode: ChangeMentoringConfirmationMode;
};
const TITLE_BY_MODE: Record<ChangeMentoringConfirmationMode, string> = {
  schedule: 'Potwierdź zmianę mentoringu',
  replace: 'Potwierdź zmianę mentoringu',
  takeBack: 'Odwołaj zaplanowaną zmianę',
  unchangedInForce: 'Ustawienia się nie zmienią',
  unchangedScheduled: 'Ustawienia już zaplanowane'
};

/**
 * The step between picking terms and filing them. Everything staff is
 * committing to is on one screen - who, from what to what, for how much, and the
 * date it starts - because the change is invisible until the renewal and there is
 * no other screen that lists it.
 */
export function ChangeMentoringConfirmAlert(t0) {
  const $ = _c(45);
  const {
    isOpen,
    onClose,
    onConfirm,
    isPending,
    studentName,
    currentTermsSummary,
    newTermsSummary,
    newPriceZloty,
    renewsAt,
    mode
  } = t0;
  let t1;
  if ($[0] !== renewsAt) {
    t1 = formatWarsawLongDate(renewsAt);
    $[0] = renewsAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const effectiveAtLabel = t1;
  let t2;
  bb0: {
    if (mode === "unchangedInForce") {
      t2 = "Wybrane warunki ju\u017C obowi\u0105zuj\u0105. Potwierdzenie niczego nie zmieni i nie utworzy eventu CRM.";
      break bb0;
    }
    if (mode === "unchangedScheduled") {
      t2 = `Te warunki już czekają na ${effectiveAtLabel}. Potwierdzenie niczego nie zmieni i nie utworzy eventu CRM.`;
      break bb0;
    }
    if (mode === "takeBack") {
      t2 = `To są warunki, które ${studentName} ma teraz. Zapisanie ich odwołuje zmianę czekającą na ${effectiveAtLabel} - uczeń zostaje na dotychczasowym mentoringu i cenie.`;
      break bb0;
    }
    t2 = `Zmieniasz ustawienia mentoringu dla ${studentName}. Nowe warunki wchodzą w życie ${effectiveAtLabel} - do tej daty nic się nie zmienia i nie wystawiamy korekty.`;
  }
  const description = t2;
  const isUnchanged = mode === "unchangedInForce" || mode === "unchangedScheduled";
  const t3 = TITLE_BY_MODE[mode];
  let t4;
  if ($[2] !== onClose || $[3] !== t3) {
    t4 = <AlertTitle handleIsOpen={onClose}>{t3}</AlertTitle>;
    $[2] = onClose;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== description) {
    t5 = <AlertDescription>{description}</AlertDescription>;
    $[5] = description;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DescriptionTerm>Teraz</DescriptionTerm>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== currentTermsSummary) {
    t7 = <DescriptionDetails>{currentTermsSummary}</DescriptionDetails>;
    $[8] = currentTermsSummary;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = mode === "unchangedScheduled" ? "Ju\u017C zaplanowano" : mode === "unchangedInForce" ? "Obowi\u0105zuje" : mode === "takeBack" ? "Zostaje" : "Po zmianie";
  let t9;
  if ($[10] !== t8) {
    t9 = <DescriptionTerm>{t8}</DescriptionTerm>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== newTermsSummary) {
    t10 = <DescriptionDetails>{newTermsSummary}</DescriptionDetails>;
    $[12] = newTermsSummary;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== effectiveAtLabel || $[15] !== mode || $[16] !== newPriceZloty) {
    t11 = mode === "unchangedInForce" ? null : <><DescriptionTerm>{mode === "unchangedScheduled" ? "Zaplanowana cena" : mode === "takeBack" ? "Cena bez zmian" : "Cena odnowienia"}</DescriptionTerm><DescriptionDetails>{formatZloty(newPriceZloty)} - w cenie 23% VAT</DescriptionDetails><DescriptionTerm>{mode === "takeBack" ? "Odnowienie" : "Obowi\u0105zuje od"}</DescriptionTerm><DescriptionDetails>{effectiveAtLabel}</DescriptionDetails></>;
    $[14] = effectiveAtLabel;
    $[15] = mode;
    $[16] = newPriceZloty;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t7 || $[21] !== t9) {
    t12 = <DescriptionList>{t6}{t7}{t9}{t10}{t11}</DescriptionList>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t7;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== mode) {
    t13 = mode === "replace" ? <p className="pt-3 text-contentSecondary text-sm">Ta zmiana zastępuje zmianę, która już czeka na to odnowienie. Data odnowienia się nie przesuwa.</p> : null;
    $[23] = mode;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <AlertBody>{t12}{t13}</AlertBody>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isPending || $[29] !== onClose) {
    t15 = <Button plain={true} type="button" onClick={onClose} disabled={isPending}>Anuluj</Button>;
    $[28] = isPending;
    $[29] = onClose;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = isUnchanged ? "Potwierd\u017A bez zmian" : mode === "takeBack" ? "Odwo\u0142aj zmian\u0119" : "Zapisz zmian\u0119";
  let t17;
  if ($[31] !== isPending || $[32] !== onConfirm || $[33] !== t16) {
    t17 = <Button type="button" onClick={onConfirm} loading={isPending}>{t16}</Button>;
    $[31] = isPending;
    $[32] = onConfirm;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t15 || $[36] !== t17) {
    t18 = <AlertActions>{t15}{t17}</AlertActions>;
    $[35] = t15;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isOpen || $[39] !== onClose || $[40] !== t14 || $[41] !== t18 || $[42] !== t4 || $[43] !== t5) {
    t19 = <Alert open={isOpen} onClose={onClose} size="lg">{t4}{t5}{t14}{t18}</Alert>;
    $[38] = isOpen;
    $[39] = onClose;
    $[40] = t14;
    $[41] = t18;
    $[42] = t4;
    $[43] = t5;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}